// src/context/AuthContext.tsx - COMPLETE WITH ALL FUNCTIONS

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService, LoginCredentials, getAspirantDetails } from '../services/api';
import toast from 'react-hot-toast';

// Define types properly
interface User {
  id: number;
  username: string;
  email: string;
  fullName: string;
  role: 'ADMIN' | 'STAFF' | 'ASPIRANT' | 'CITIZEN';
  phoneNumber: string;
}

interface Aspirant {
  id: number;
  name: string;
  phoneNumber: string;
  email: string;
  position: string;
  constituency: string;
  wardId: string;
  wardName?: string;
  constituencyName?: string;
  party: string;
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  subscriptionDaysLeft?: string;
  tier?: string;
  languagePreference?: string;
  receiveSmsAlerts?: boolean;
  receiveEmailAlerts?: boolean;
}

interface Citizen {
  id: number;
  phoneNumber: string;
  fullName: string;
  email?: string;
  idNumber?: string;
  gender?: string;
  dateOfBirth?: string;
  wardId?: number;
  wardName?: string;
  constituencyId?: number;
  constituencyName?: string;
  physicalAddress?: string;
  voterRegistrationNumber?: string;
  pollingStation?: string;
  languagePreference?: string;
  receiveSmsAlerts?: boolean;
  receiveEmailAlerts?: boolean;
  verified?: boolean;
  active?: boolean;
  createdAt?: string;
  reportCount?: number;
}

// Define response types
interface AuthResponseData {
  token: string;
  refreshToken?: string;
  user?: {
    id: number;
    username: string;
    email: string;
    fullName: string;
    role: string;
    phoneNumber: string;
  };
  citizen?: Citizen;
  aspirant?: Aspirant;
}

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
}

interface AuthContextType {
  user: User | null;
  aspirant: Aspirant | null;
  citizen: Citizen | null;
  token: string | null;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<User>;
  loginAspirant: (phoneNumber: string, otp: string) => Promise<User>;
  loginCitizen: (phoneNumber: string, verificationCode: string) => Promise<{ user: User; citizen: Citizen }>;
  loginCitizenWithEmailPassword: (email: string, password: string) => Promise<{ user: User; citizen: Citizen }>;
  citizenForgotPassword: (email: string) => Promise<void>;
  citizenResetPassword: (email: string, otp: string, newPassword: string) => Promise<void>;
  logout: () => void;
  sendOtp: (phoneNumber: string) => Promise<void>;
  requestVerificationCode: (phoneNumber: string) => Promise<void>;
  aspirantSignup: (data: any) => Promise<any>;
  citizenSignup: (data: any) => Promise<any>;
  clearAuth: () => void;
  refreshAuth: () => void;
  refreshAspirantDetails: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Helper function to transform API user to User type
const transformApiUser = (apiUser: any): User => {
  const validRoles: Array<'ADMIN' | 'STAFF' | 'ASPIRANT' | 'CITIZEN'> = 
    ['ADMIN', 'STAFF', 'ASPIRANT', 'CITIZEN'];
  
  const role = validRoles.includes(apiUser?.role) 
    ? apiUser.role as 'ADMIN' | 'STAFF' | 'ASPIRANT' | 'CITIZEN'
    : 'CITIZEN';
  
  return {
    id: apiUser?.id || 0,
    username: apiUser?.username || apiUser?.phoneNumber || '',
    email: apiUser?.email || '',
    fullName: apiUser?.fullName || '',
    role: role,
    phoneNumber: apiUser?.phoneNumber || ''
  };
};

// Format phone number helper
const formatPhoneNumber = (phone: string): string => {
  let cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.startsWith('0')) {
    cleaned = `254${cleaned.slice(1)}`;
  } else if (!cleaned.startsWith('254')) {
    cleaned = `254${cleaned}`;
  }
  
  return cleaned;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [aspirant, setAspirant] = useState<Aspirant | null>(null);
  const [citizen, setCitizen] = useState<Citizen | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Initialize auth from localStorage - ONCE on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('🔄 Initializing auth from localStorage...');
        
        const savedToken = localStorage.getItem('token');
        const savedUser = localStorage.getItem('user');
        const savedAspirant = localStorage.getItem('aspirant');
        const savedCitizen = localStorage.getItem('citizen');

        if (savedToken && savedUser) {
          setToken(savedToken);
          const parsedUser = JSON.parse(savedUser);
          const typedUser = transformApiUser(parsedUser);
          setUser(typedUser);
          
          if (savedAspirant) {
            const parsedAspirant = JSON.parse(savedAspirant);
            setAspirant(parsedAspirant);
            
            if (parsedAspirant && !parsedAspirant.wardName) {
              setTimeout(() => refreshAspirantDetails(), 1000);
            }
          }
          
          if (savedCitizen) {
            setCitizen(JSON.parse(savedCitizen));
          }
          
          console.log('✅ Auth initialized successfully');
        }
      } catch (error) {
        console.error('❌ Error initializing auth:', error);
        clearAuth();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Refresh aspirant details
  const refreshAspirantDetails = useCallback(async () => {
    try {
      if (!aspirant?.id) {
        console.log('⚠️ No aspirant ID available to refresh details');
        return;
      }

      console.log(`🔄 Refreshing aspirant details for ID: ${aspirant.id}`);
      
      const response = await getAspirantDetails(aspirant.id.toString());
      
      if (response.success && response.data) {
        const aspirantData = response.data;
        
        const updatedAspirant: Aspirant = {
          ...aspirant,
          wardName: aspirantData.wardName || aspirantData.ward?.name,
          wardId: aspirantData.wardId?.toString() || aspirantData.ward?.id?.toString() || aspirant.wardId,
          constituencyName: aspirantData.constituencyName || aspirantData.constituency?.name,
          constituency: aspirantData.constituencyName || aspirantData.constituency?.name || aspirant.constituency,
          position: aspirantData.position || aspirant.position,
          party: aspirantData.party || aspirant.party,
          email: aspirantData.email || aspirant.email,
          name: aspirantData.name || aspirant.name,
          phoneNumber: aspirantData.phoneNumber || aspirant.phoneNumber,
        };
        
        setAspirant(updatedAspirant);
        localStorage.setItem('aspirant', JSON.stringify(updatedAspirant));
        
        console.log('✅ Aspirant details refreshed successfully');
      }
    } catch (error) {
      console.error('❌ Error refreshing aspirant details:', error);
    }
  }, [aspirant]);

  const refreshAuth = useCallback(() => {
    console.log('🔄 Refreshing auth state...');
    
    const savedToken = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    const savedAspirant = localStorage.getItem('aspirant');
    const savedCitizen = localStorage.getItem('citizen');
    
    if (savedToken && savedUser) {
      setToken(savedToken);
      setUser(JSON.parse(savedUser));
      
      if (savedAspirant) {
        const parsedAspirant = JSON.parse(savedAspirant);
        setAspirant(parsedAspirant);
        
        if (parsedAspirant && !parsedAspirant.wardName) {
          setTimeout(() => refreshAspirantDetails(), 1000);
        }
      }
      
      if (savedCitizen) {
        setCitizen(JSON.parse(savedCitizen));
      }
      
      console.log('✅ Auth refreshed');
    }
  }, [refreshAspirantDetails]);

  const clearAuth = useCallback(() => {
    console.log('🧹 Clearing auth data...');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('aspirant');
    localStorage.removeItem('citizen');
    localStorage.removeItem('refreshToken');
    
    setToken(null);
    setUser(null);
    setAspirant(null);
    setCitizen(null);
  }, []);

  // Admin/Staff Login
  const login = async (credentials: LoginCredentials): Promise<User> => {
    try {
      console.log('🔐 Attempting admin login...');
      
      const response = await apiService.login(credentials);
      const responseData = response.data as ApiResponse<AuthResponseData>;
      
      if (responseData.success === false) {
        throw new Error(responseData.error || responseData.message || 'Login failed');
      }
      
      const authData = responseData.data || responseData;
      
      if (!authData || typeof authData !== 'object') {
        throw new Error('Invalid response format');
      }
      
      const hasToken = 'token' in authData;
      if (!hasToken) {
        throw new Error('No token received from server');
      }
      
      const extractedData = authData as any;
      const token = extractedData.token;
      const apiUser = extractedData.user;
      
      if (!apiUser) {
        const typedUser: User = {
          id: Date.now(),
          username: credentials.email.split('@')[0] || 'admin',
          email: credentials.email,
          fullName: 'Administrator',
          role: 'ADMIN',
          phoneNumber: ''
        };
        
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(typedUser));
        
        setToken(token);
        setUser(typedUser);
        setAspirant(null);
        setCitizen(null);
        
        toast.success('Login successful!');
        navigate('/admin/dashboard');
        
        return typedUser;
      }
      
      const typedUser = transformApiUser(apiUser);
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(typedUser));
      
      setToken(token);
      setUser(typedUser);
      setAspirant(null);
      setCitizen(null);
      
      toast.success('Login successful!');
      
      if (typedUser.role === 'ADMIN' || typedUser.role === 'STAFF') {
        navigate('/admin/dashboard');
      }
      
      return typedUser;
    } catch (error: any) {
      console.error('❌ Login error:', error);
      toast.error(error.response?.data?.error || error.message || 'Login failed');
      throw error;
    }
  };

  // Aspirant OTP Send
  const sendOtp = async (phoneNumber: string) => {
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      console.log('📱 Sending OTP to:', formattedPhone);
      
      await apiService.sendAspirantOtp(formattedPhone);
      toast.success('OTP sent successfully');
    } catch (error: any) {
      console.error('❌ OTP send error:', error);
      toast.error(error.response?.data?.error || 'Failed to send OTP');
      throw error;
    }
  };

  // Legacy OTP for citizen
  const requestVerificationCode = async (phoneNumber: string): Promise<void> => {
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      console.log('📱 Requesting verification code for:', formattedPhone);
      
      const response = await apiService.requestVerificationCode(formattedPhone);
      
      if (response.data.success) {
        toast.success('Verification code sent!');
      } else {
        throw new Error(response.data.error || 'Failed to send verification code');
      }
    } catch (error: any) {
      console.error('❌ Verification code request error:', error);
      
      if (error.response?.status === 404) {
        throw new Error('Citizen not found. Please register first.');
      } else {
        const errorMsg = error.response?.data?.error || error.message || 'Failed to send verification code';
        toast.error(errorMsg);
        throw new Error(errorMsg);
      }
    }
  };

  // Legacy OTP login for citizen
  const loginCitizen = async (phoneNumber: string, verificationCode: string): Promise<{ user: User; citizen: Citizen }> => {
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      console.log('🔐 Attempting citizen login with OTP...');
      
      const response = await apiService.verifyCitizen({
        phoneNumber: formattedPhone,
        verificationCode
      });
      
      const responseData = response.data as ApiResponse<AuthResponseData>;
      
      if (!responseData.success) {
        throw new Error(responseData.error || 'Verification failed');
      }
      
      const authData = responseData.data || responseData;
      
      if (!authData || typeof authData !== 'object') {
        throw new Error('Invalid response format');
      }
      
      const hasToken = 'token' in authData;
      if (!hasToken) {
        throw new Error('No token received from server');
      }
      
      const extractedData = authData as any;
      const token = extractedData.token;
      const citizenData = extractedData.citizen;
      
      if (!citizenData) {
        throw new Error('No citizen data received');
      }
      
      const userObj: User = {
        id: citizenData.id || 0,
        username: citizenData.phoneNumber || formattedPhone,
        email: citizenData.email || '',
        fullName: citizenData.fullName || 'Citizen User',
        role: 'CITIZEN',
        phoneNumber: citizenData.phoneNumber || formattedPhone
      };
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userObj));
      localStorage.setItem('citizen', JSON.stringify(citizenData));
      
      setToken(token);
      setUser(userObj);
      setCitizen(citizenData);
      setAspirant(null);
      
      toast.success('Welcome back!');
      navigate('/citizen/dashboard');
      
      return { user: userObj, citizen: citizenData };
    } catch (error: any) {
      console.error('❌ Citizen login error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Invalid verification code. Please try again.');
      } else if (error.response?.status === 400) {
        toast.error('Verification code expired. Please request a new one.');
      } else {
        toast.error(error.response?.data?.error || error.message || 'Login failed');
      }
      
      throw error;
    }
  };

  // ============ NEW: Citizen login with email/password ============
  const loginCitizenWithEmailPassword = async (email: string, password: string): Promise<{ user: User; citizen: Citizen }> => {
    try {
      console.log('🔐 Attempting citizen login with email/password...');
      
      // Make sure we're not sending any auth token for this request
      // The apiService will handle this properly
      const response = await apiService.citizenLoginWithEmailPassword({ email, password });
      
      const responseData = response.data as ApiResponse<AuthResponseData>;
      
      if (!responseData.success) {
        throw new Error(responseData.error || 'Login failed');
      }
      
      const authData = responseData.data || responseData;
      
      if (!authData || typeof authData !== 'object') {
        throw new Error('Invalid response format');
      }
      
      const hasToken = 'token' in authData;
      if (!hasToken) {
        throw new Error('No token received from server');
      }
      
      const extractedData = authData as any;
      const token = extractedData.token;
      const citizenData = extractedData.citizen;
      
      if (!citizenData) {
        throw new Error('No citizen data received');
      }
      
      // Extract phone number from multiple possible locations
      let phoneNumber = citizenData.phoneNumber || citizenData.phone_number;
      
      if (!phoneNumber && token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          phoneNumber = payload.phoneNumber || payload.sub;
          if (phoneNumber && !phoneNumber.includes('@')) {
            console.log('✅ Retrieved phone number from JWT token:', phoneNumber);
          }
        } catch (e) {
          console.error('Error parsing token for phone number:', e);
        }
      }
      
      const userObj: User = {
        id: citizenData.id || 0,
        username: citizenData.email || email,
        email: citizenData.email || email,
        fullName: citizenData.fullName || 'Citizen User',
        role: 'CITIZEN',
        phoneNumber: phoneNumber || ''
      };
      
      const citizenToStore = {
        ...citizenData,
        phoneNumber: phoneNumber || citizenData.phoneNumber || citizenData.phone_number || '',
        id: citizenData.id,
        fullName: citizenData.fullName,
        email: citizenData.email
      };
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(userObj));
      localStorage.setItem('citizen', JSON.stringify(citizenToStore));
      
      if (extractedData.refreshToken) {
        localStorage.setItem('refreshToken', extractedData.refreshToken);
      }
      
      setToken(token);
      setUser(userObj);
      setCitizen(citizenToStore);
      setAspirant(null);
      
      console.log('✅ Login successful, citizen data stored');
      
      toast.success('Welcome back!');
      navigate('/citizen/dashboard');
      
      return { user: userObj, citizen: citizenToStore };
    } catch (error: any) {
      console.error('❌ Citizen login error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Invalid email or password');
      } else if (error.response?.status === 403) {
        toast.error('Account not verified. Please check your email for verification link.');
      } else {
        toast.error(error.response?.data?.error || error.message || 'Login failed');
      }
      
      throw error;
    }
  };

  // ============ NEW: Citizen forgot password ============
  const citizenForgotPassword = async (email: string): Promise<void> => {
    try {
      console.log('📧 Requesting password reset for:', email);
      
      const response = await apiService.requestPasswordReset(email);
      
      if (response.data.success) {
        toast.success('Password reset code sent to your email!', {
          duration: 5000,
          icon: '📧',
        });
      } else {
        throw new Error(response.data.error || 'Failed to send reset code');
      }
    } catch (error: any) {
      console.error('❌ Password reset request error:', error);
      
      const errorMsg = error.response?.data?.error || error.message || 'Failed to send reset code';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
  };

  // ============ NEW: Citizen reset password ============
  const citizenResetPassword = async (email: string, otp: string, newPassword: string): Promise<void> => {
    try {
      console.log('🔐 Resetting password for:', email);
      
      const response = await apiService.resetCitizenPassword(email, otp, newPassword);
      
      if (response.data.success) {
        toast.success('Password reset successful! Please login with your new password.', {
          duration: 5000,
        });
      } else {
        throw new Error(response.data.error || 'Failed to reset password');
      }
    } catch (error: any) {
      console.error('❌ Password reset error:', error);
      
      const errorMsg = error.response?.data?.error || error.message || 'Failed to reset password';
      toast.error(errorMsg);
      throw new Error(errorMsg);
    }
  };

  // Citizen signup
  const citizenSignup = async (data: any): Promise<any> => {
    try {
      console.log('📝 Attempting citizen signup...');
      
      if (data.phoneNumber) {
        data.phoneNumber = formatPhoneNumber(data.phoneNumber);
      }
      
      const response = await apiService.registerCitizen(data);
      
      if (response.data.success) {
        toast.success('Registration successful! Please login with your credentials.', {
          duration: 5000,
        });
        return response.data;
      } else {
        throw new Error(response.data.error || 'Registration failed');
      }
    } catch (error: any) {
      console.error('❌ Citizen signup error:', error);
      
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(key => {
          toast.error(`${key}: ${errors[key].join(', ')}`);
        });
      } else {
        toast.error(error.response?.data?.error || 'Registration failed');
      }
      
      throw error;
    }
  };

  // Aspirant login with OTP - FIXED
  const loginAspirant = async (phoneNumber: string, otp: string): Promise<User> => {
    try {
      const formattedPhone = formatPhoneNumber(phoneNumber);
      console.log('🔐 Attempting aspirant login...');
      console.log('📱 Phone:', formattedPhone);
      console.log('🔑 OTP:', otp);
      
      const response = await apiService.aspirantLogin({ 
        phoneNumber: formattedPhone, 
        otp 
      });
      
      console.log('📦 Raw response:', response);
      
      const responseData = response.data as ApiResponse<AuthResponseData>;
      
      if (!responseData.success) {
        throw new Error(responseData.error || 'Login failed');
      }
      
      const authData = responseData.data || responseData;
      
      if (!authData || typeof authData !== 'object') {
        throw new Error('Invalid response format');
      }
      
      const hasToken = 'token' in authData;
      if (!hasToken) {
        throw new Error('No token received');
      }
      
      const extractedData = authData as any;
      const token = extractedData.token;
      const apiUser = extractedData.user;
      const aspirantData = extractedData.aspirant;
      
      console.log('🎯 Token received:', token ? 'Yes' : 'No');
      console.log('🎯 User data:', apiUser);
      console.log('🎯 Aspirant data:', aspirantData);
      
      let typedUser: User;
      let aspirantProfile: Aspirant;
      
      if (!apiUser) {
        typedUser = {
          id: aspirantData?.id || Date.now(),
          username: formattedPhone,
          email: aspirantData?.email || '',
          fullName: aspirantData?.name || `Aspirant ${formattedPhone}`,
          role: 'ASPIRANT',
          phoneNumber: formattedPhone
        };
      } else {
        typedUser = transformApiUser(apiUser);
      }
      
      if (aspirantData) {
        aspirantProfile = {
          id: aspirantData.id || typedUser.id,
          name: aspirantData.name || typedUser.fullName,
          phoneNumber: aspirantData.phoneNumber || formattedPhone,
          email: aspirantData.email || typedUser.email,
          position: aspirantData.position || 'Aspirant',
          constituency: aspirantData.constituency || aspirantData.constituencyName || '',
          wardId: aspirantData.wardId?.toString() || aspirantData.ward?.id?.toString() || '',
          wardName: aspirantData.wardName || aspirantData.ward?.name,
          constituencyName: aspirantData.constituencyName || aspirantData.constituency?.name,
          party: aspirantData.party || '',
          languagePreference: aspirantData.languagePreference || 'en',
          receiveSmsAlerts: aspirantData.receiveSmsAlerts !== undefined ? aspirantData.receiveSmsAlerts : true,
          receiveEmailAlerts: aspirantData.receiveEmailAlerts !== undefined ? aspirantData.receiveEmailAlerts : true,
          subscriptionStartDate: aspirantData.subscriptionStartDate,
          subscriptionEndDate: aspirantData.subscriptionEndDate,
          subscriptionDaysLeft: aspirantData.subscriptionDaysLeft,
          tier: aspirantData.tier || 'BASIC'
        };
      } else {
        aspirantProfile = {
          id: typedUser.id,
          name: typedUser.fullName,
          phoneNumber: formattedPhone,
          email: typedUser.email,
          position: 'Aspirant',
          constituency: '',
          wardId: '',
          party: '',
          languagePreference: 'en',
          receiveSmsAlerts: true,
          receiveEmailAlerts: true
        };
      }
      
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(typedUser));
      localStorage.setItem('aspirant', JSON.stringify(aspirantProfile));
      
      if (extractedData.refreshToken) {
        localStorage.setItem('refreshToken', extractedData.refreshToken);
      }
      
      setToken(token);
      setUser(typedUser);
      setAspirant(aspirantProfile);
      setCitizen(null);
      
      toast.success('Login successful!');
      navigate('/aspirant/dashboard');
      
      if (aspirantProfile.id && !aspirantProfile.wardName) {
        setTimeout(() => refreshAspirantDetails(), 2000);
      }
      
      return typedUser;
    } catch (error: any) {
      console.error('❌ Aspirant login error:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
      
      let errorMessage = 'Login failed';
      if (error.response?.status === 401) {
        errorMessage = 'Invalid OTP. Please try again.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      throw error;
    }
  };

  const aspirantSignup = async (data: any): Promise<any> => {
    try {
      console.log('📝 Attempting aspirant signup...');
      
      if (data.phoneNumber) {
        data.phoneNumber = formatPhoneNumber(data.phoneNumber);
      }
      
      const response = await apiService.aspirantSignup(data);
      toast.success('Registration successful!');
      return response.data;
    } catch (error: any) {
      console.error('❌ Aspirant signup error:', error);
      toast.error(error.response?.data?.error || 'Registration failed');
      throw error;
    }
  };

  const logout = useCallback(() => {
    console.log('👋 Logging out...');
    clearAuth();
    toast.success('Logged out successfully');
    navigate('/login');
  }, [clearAuth, navigate]);

  return (
    <AuthContext.Provider value={{
      user,
      aspirant,
      citizen,
      token,
      isLoading,
      login,
      loginAspirant,
      loginCitizen,
      loginCitizenWithEmailPassword,
      citizenForgotPassword,
      citizenResetPassword,
      logout,
      sendOtp,
      requestVerificationCode,
      aspirantSignup,
      citizenSignup,
      clearAuth,
      refreshAuth,
      refreshAspirantDetails
    }}>
      {children}
    </AuthContext.Provider>
  );
};